const BIELIK_GUARD_KEY = 'prompt.moderation.enabled';
const TWO_FACTOR_AUTH_KEY = 'two.factor.auth';

type ConfigType = 'boolean' | 'string';

export interface ConfigDefinition {
    labelKey: string;
    descriptionKey?: string;
    type: ConfigType;
}

export const EXTERNAL_LOGIN_ERROR_MESSAGE: Record<string, string> = {
    no_code: 'login.noCodeError',
    auth_failed: 'login.authFailedError',
};

export const CONFIG_DEFINITIONS: Record<string, ConfigDefinition> = {
    [BIELIK_GUARD_KEY]: {
        labelKey: 'configuration.bielikGuard',
        descriptionKey: 'configuration.bielikGuardDescription',
        type: 'boolean',
    },
    [TWO_FACTOR_AUTH_KEY]: {
        labelKey: 'configuration.twoFactorAuth',
        descriptionKey: 'configuration.twoFactorAuthDescription',
        type: 'boolean',
    },
};

export const FEATURE_POINTER_KEY = 'feature';

export const FEATURE_KEYS = {
    OCR_KEY: 'feature.ocr.enabled',
    TRANSLATION_KEY: 'feature.translation.enabled',
    DOCUMENT_ANALYSIS_KEY: 'feature.document.analysis.enabled',
    DOCUMENT_DIGITIZATION_KEY: 'feature.document.digitalisation.enabled',
    SHAREPOINT_KEY: 'feature.sharepoint.enabled',
    ACTIVE_DIRECTORY_KEY: 'feature.active-directory.enabled',
};

export type FeatureKey = typeof FEATURE_KEYS[keyof typeof FEATURE_KEYS];

export const FEATURE_ACCESS_CONFIG_ORDER: FeatureKey[] = [
    FEATURE_KEYS.OCR_KEY,
    FEATURE_KEYS.TRANSLATION_KEY,
    FEATURE_KEYS.DOCUMENT_ANALYSIS_KEY,
    FEATURE_KEYS.DOCUMENT_DIGITIZATION_KEY,
    FEATURE_KEYS.SHAREPOINT_KEY,
    FEATURE_KEYS.ACTIVE_DIRECTORY_KEY
];

export const FEATURE_ACCESS_CONFIG_DEFINITIONS: Record<FeatureKey, ConfigDefinition> = {
    [FEATURE_KEYS.OCR_KEY]: {
        labelKey: 'configuration.features.ocr',
        descriptionKey: 'configuration.features.ocrDescription',
        type: 'boolean',
    },
    [FEATURE_KEYS.TRANSLATION_KEY]: {
        labelKey: 'configuration.features.translation',
        descriptionKey: 'configuration.features.translationDescription',
        type: 'boolean',
    },
    [FEATURE_KEYS.DOCUMENT_ANALYSIS_KEY]: {
        labelKey: 'configuration.features.documentAnalysis',
        descriptionKey: 'configuration.features.documentAnalysisDescription',
        type: 'boolean',
    },
    [FEATURE_KEYS.DOCUMENT_DIGITIZATION_KEY]: {
        labelKey: 'configuration.features.documentDigitization',
        descriptionKey: 'configuration.features.documentDigitizationDescription',
        type: 'boolean',
    },
    [FEATURE_KEYS.SHAREPOINT_KEY]: {
        labelKey: 'configuration.features.sharePoint',
        descriptionKey: 'configuration.features.sharePointDescription',
        type: 'boolean',
    },
    [FEATURE_KEYS.ACTIVE_DIRECTORY_KEY]: {
        labelKey: 'configuration.features.activeDirectory',
        descriptionKey: 'configuration.features.activeDirectoryDescription',
        type: 'boolean',
    },
};

export type ConfigurationEntry = {
    propertyName:   string;
    propertyValue:  string;
    updatedAt:  string;
};

export interface Model {
    id: number;
    displayName: string;
    description: string;
    isDefault: boolean;
    canThink: boolean;
    canUseTools: boolean;
}

export type AD_GROUP_ROLE =
    | 'NOT_ASSIGNED'
    | 'USER'
    | 'SYSTEM_ADMIN'
    | 'DOCUMENTS_ADMIN';

export const AD_VALID_ROLES: AD_GROUP_ROLE[] = ["USER", "SYSTEM_ADMIN", "DOCUMENTS_ADMIN"];

export interface ActiveDirectoryProperties {
    baseDn: string;
    code: string;
    groupMemberAttribute: string;
    groupNameAttribute: string;
    groupSearchBase: string;
    memberOfAttribute: string;
    serviceAccountDn: string;
    serviceAccountPassword: string;
    url: string;
    userLoginAttribute: string;
    userObjectClass: string;
    userSearchBase: string;
    nestedGroupsEnabled: boolean;
    certificateFileName: string;
    certificateContent: string;
}

// POST ad/properties
export interface ActiveDirectoryConnectionCode {
    adConnectionPropertiesCode: string;
}

// GET ad/properties/code
export interface ActiveDirectoryCode {
    code: string;
}

export interface ActiveDirectoryGroup {
    groupId: number;
    groupName: string;
}

// name of group and group role assigned to a name
export interface ActiveDirectoryGroupRoles {
    groupRoles: Record<string, AD_GROUP_ROLE>;
}

export interface ActiveDirectoryGroupWithRole extends ActiveDirectoryGroup {
    groupRole: AD_GROUP_ROLE | '';
}

export interface SharepointConfigDocuments {
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
    size: number;
    empty: boolean;

    number: number;
    numberOfElements: number;
    sort: {
        empty: boolean;
        sorted: boolean;
        unsorted: boolean;
    };
    pageable: {
        offset: number;
        sort: {
            empty: boolean;
            sorted: boolean;
            unsorted: boolean;
        };
        pageNumber: number;
        paged: boolean;
        pageSize: number;
        unpaged: boolean;
    };

    content: string[]; // document names
}

export interface CurrentUserProfileRequest {
    firstName?: string;
    lastName?: string;
    position?: string;
    phoneNumber?: string;
}

// GET /sharepoint/properties
export interface SharePointPropertiesData {
    maxFileSizeBytes: number;
    tenantId: string;
    clientId: string;
    clientSecret: string;
    siteId: string;
    driveId: string;
}

// POST /sharepoint/properties
export interface SharePointPropertiesId {
    sharepointPropertiesId: number;
}

export interface AuthEntraExchangeResponse {
    accessToken: string;
    refreshToken: string;
    username: string;
    redirectPath: string;
}